import React, { useState } from 'react';
import dayjs from 'dayjs';
import { Table } from 'react-bootstrap';
import { NewsEvent } from '@/modules/calendar/model/calendar/news-event';
import { DataRelease } from '@/modules/calendar/model/calendar/data-release';
import DataReleaseHistoryChart from '@/modules/calendar/components/data/impact-charting/data-impact-chart';
import { RequirePermissions, Permissions } from '@/core/permissions/export';
import { FormattingUtil } from '@/modules/calendar/util/formatting-util';
import { DataSeriesEntry } from '@/modules/calendar/model/calendar/data-series-entry';

export default function DataReleaseHistoryTable({
  events,
  selectedRelease,
  highlightDate,
}: {
  events: NewsEvent[];
  selectedRelease: DataRelease;
  highlightDate?: string;
}) {
  const [openEventId, setOpenEventId] = useState<string | undefined>(undefined);

  const toggleOpen = (id: string) => {
    setOpenEventId(prev => (prev === id ? undefined : id));
  };

  const getActual = (dataSeriesEntries: DataSeriesEntry[], id: number): React.ReactNode => {
    const entry = dataSeriesEntries.find(value => `${value.dataSeriesId}` === `${id}`);
    if (!entry) return null;

    return FormattingUtil.formatNumber(entry.actual, entry.precision);
  };

  const highlightMoment = highlightDate ? dayjs(highlightDate) : undefined;

  return (
    <Table striped hover size="sm" responsive className={'small '}>
      <thead>
        <tr>
          <th>Date</th>

          {selectedRelease.series.map(series => (
            <th key={series.id} className={'text-end'}>
              {series.display}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {events
          .sort((a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf())
          .map(event => {
            const isHighlighted = !!highlightMoment && dayjs(event.date).isSame(highlightMoment);

            return (
              <React.Fragment key={event.id}>
                <tr
                  onClick={() => {
                    toggleOpen(event.id);
                  }}
                  className={isHighlighted ? 'table-warning' : undefined}
                  style={{ cursor: 'pointer' }}
                  role="button"
                >
                  <th className={'text-nowrap'}>{dayjs(event.date).format('YYYY-MM-DD')}</th>

                  {selectedRelease.series.map(series => (
                    <td className={'text-end'} key={`${event.id}-${series.id}`}>
                      {getActual(event.dataSeriesEntries, series.id)}
                    </td>
                  ))}
                </tr>

                {openEventId === event.id && (
                  <RequirePermissions allow={Permissions.ALPHA_ACCESS}>
                    <tr className="table-active">
                      <td colSpan={1 + selectedRelease.series.length}>
                        <div className="mt-2" style={{ height: 480 }}>
                          <DataReleaseHistoryChart
                            selectedRelease={selectedRelease}
                            eventDate={dayjs(event.date)
                              .tz('America/New_York')
                              .format('YYYY-MM-DDTHH:mm:ss')}
                          />
                        </div>
                      </td>
                    </tr>
                  </RequirePermissions>
                )}
              </React.Fragment>
            );
          })}
      </tbody>
    </Table>
  );
}
